import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ShoppingCart, 
  Package, 
  Users, 
  UserCircle, 
  Truck, 
  ClipboardText, 
  ChartBar, 
  SignOut,
  SquaresFour,
  Tag,
  List,
  X,
  CashRegister,
  Calculator,
  CurrencyDollar,
  ShoppingBag,
  Bookmark,
  ArrowSquareOut,
  BookOpen,
  Gear,
  QrCode,
  Lock,
  LockOpen,
  CaretDown,
  CaretRight
} from '@phosphor-icons/react';

// Orden fijo que los cajeros (rol != admin) verán siempre visible.
// El resto de las páginas se agrupan bajo "🔒 Oculto" y requieren clave admin.
const CLERK_VISIBLE_ORDER = [
  '/pos',
  '/cuts',
  '/products',
  '/packages',
  '/shipments',
  '/collect-shipments',
  '/shipping-costs',
  '/purchases',
  '/exits',
  '/transferencias',
  '/apartados',
  '/clients',
  '/sellers',
  '/inventory',
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Sidebar = ({ mobileOpen = false, onClose = () => {} }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [lowStockCount, setLowStockCount] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  const [myBranch, setMyBranch] = useState(null);
  const [incomingTransfersCount, setIncomingTransfersCount] = useState(0);
  const [menuOrder, setMenuOrder] = useState(() => {
    // Hidratamos desde localStorage para evitar el flash del orden predeterminado
    // mientras se carga la respuesta del backend.
    try {
      const cached = localStorage.getItem('pos_menu_order_v1');
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });

  useEffect(() => {
    fetchLowStockCount();
    axios.get(`${API}/config/menu-order`, { withCredentials: true })
      .then(({ data }) => {
        const order = data.order || [];
        setMenuOrder(order);
        try { localStorage.setItem('pos_menu_order_v1', JSON.stringify(order)); } catch { /* */ }
      })
      .catch(() => { /* mantener cache si falla */ });
    // Cargar sucursal asignada del user (multi-sucursal)
    axios.get(`${API}/auth/branch`, { withCredentials: true })
      .then(({ data }) => setMyBranch(data))
      .catch(() => setMyBranch(null));
    // Poll incoming transfers cada 30s para el badge
    const loadIncoming = () => axios.get(`${API}/transfers/incoming`, { withCredentials: true })
      .then(({ data }) => setIncomingTransfersCount((data || []).length))
      .catch(() => {});
    loadIncoming();
    const iv = setInterval(loadIncoming, 30000);
    return () => clearInterval(iv);
  }, []);

  const fetchLowStockCount = async () => {
    try {
      const { data } = await axios.get(`${API}/products/low-stock`, { withCredentials: true });
      setLowStockCount(data.length);
    } catch (error) {
      console.error('Error fetching low stock count:', error);
    }
  };

  const baseMenuItems = [
    { path: '/dashboard', icon: SquaresFour, label: 'Dashboard' },
    { path: '/pos', icon: ShoppingCart, label: 'Punto de Venta' },
    { path: '/products', icon: Package, label: 'Productos' },
    { path: '/packages', icon: Package, label: 'Paquetes' },
    { path: '/productos-coreanos', icon: Package, label: 'Productos Coreanos' },
    { path: '/categories', icon: Tag, label: 'Categorías' },
    { path: '/clients', icon: Users, label: 'Clientes' },
    { path: '/sellers', icon: UserCircle, label: 'Vendedores' },
    { path: '/shipments', icon: Truck, label: 'Envíos' },
    { path: '/apartados', icon: Bookmark, label: 'Apartados' },
    { path: '/collect-shipments', icon: CashRegister, label: 'Cobrar Envíos' },
    { path: '/shipping-costs', icon: Truck, label: 'Costos de Envío' },
    { path: '/mercado-libre', icon: ShoppingBag, label: 'ML Packs Manuales' },
    { path: '/ml-scanner', icon: QrCode, label: 'Mercado Libre' },
    { path: '/inventory', icon: ClipboardText, label: 'Inventario', badge: lowStockCount > 0 ? lowStockCount : null },
    { path: '/transferencias', icon: Package, label: 'Transferencias', badge: incomingTransfersCount > 0 ? incomingTransfersCount : null },
    { path: '/purchases', icon: ShoppingBag, label: 'Compras' },
    { path: '/exits', icon: ArrowSquareOut, label: 'Salidas' },
    { path: '/commissions', icon: CurrencyDollar, label: 'Comisiones' },
    { path: '/catalog', icon: BookOpen, label: 'Catálogo' },
    { path: '/cuts', icon: Calculator, label: 'Corte de Caja' },
    { path: '/reports', icon: ChartBar, label: 'Reportes' },
    { path: '/sales-report', icon: ChartBar, label: 'Reporte Ventas' },
    { path: '/stock-audit', icon: ClipboardText, label: 'Auditoría Stock' },
  ];

  // Aplicar orden personalizado guardado por el admin (si existe)
  const menuItems = (() => {
    if (!menuOrder || menuOrder.length === 0) return baseMenuItems;
    const byPath = Object.fromEntries(baseMenuItems.map(i => [i.path, i]));
    const ordered = menuOrder.map(p => byPath[p]).filter(Boolean);
    const remaining = baseMenuItems.filter(i => !menuOrder.includes(i.path));
    return [...ordered, ...remaining];
  })();

  // Para CAJEROS (rol != admin): dividir en visibles fijos y ocultos (requieren clave admin)
  const isClerk = user && user.role !== 'admin';
  const byPathAll = Object.fromEntries(baseMenuItems.map(i => [i.path, i]));
  const clerkVisibleItems = isClerk ? CLERK_VISIBLE_ORDER.map(p => byPathAll[p]).filter(Boolean) : [];
  const clerkHiddenItems = isClerk ? menuItems.filter(i => !CLERK_VISIBLE_ORDER.includes(i.path)) : [];

  // Estado del desbloqueo "Oculto" (persistido en sessionStorage; se pierde al cerrar sesión/pestaña)
  const [hiddenUnlocked, setHiddenUnlocked] = useState(() => {
    try { return sessionStorage.getItem('clerk_hidden_unlocked_v1') === '1'; } catch { return false; }
  });
  const [hiddenExpanded, setHiddenExpanded] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [unlockPwd, setUnlockPwd] = useState('');
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [unlockError, setUnlockError] = useState('');

  const openHiddenToggle = () => {
    if (hiddenUnlocked) {
      setHiddenExpanded(v => !v);
    } else {
      setUnlockPwd('');
      setUnlockError('');
      setShowUnlockModal(true);
    }
  };
  const submitUnlock = async (e) => {
    e?.preventDefault?.();
    if (!unlockPwd) return;
    setUnlockLoading(true);
    setUnlockError('');
    try {
      await axios.post(`${API}/auth/verify-admin-password`, { password: unlockPwd }, { withCredentials: true });
      setHiddenUnlocked(true);
      try { sessionStorage.setItem('clerk_hidden_unlocked_v1', '1'); } catch { /* */ }
      setHiddenExpanded(true);
      setShowUnlockModal(false);
      toast.success('Sección oculta desbloqueada');
    } catch (err) {
      setUnlockError(err?.response?.data?.detail || 'Clave incorrecta');
    } finally {
      setUnlockLoading(false);
    }
  };
  const lockHidden = () => {
    setHiddenUnlocked(false);
    setHiddenExpanded(false);
    try { sessionStorage.removeItem('clerk_hidden_unlocked_v1'); } catch { /* */ }
    toast.info('Sección oculta bloqueada');
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <>
      {/* Backdrop en móvil cuando el drawer está abierto */}
      <div
        className={`lg:hidden fixed inset-0 bg-black/40 z-40 transition-opacity ${mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
        data-testid="sidebar-backdrop"
      />
      <aside
        className={`
          ${collapsed ? 'w-20' : 'w-64'}
          bg-white border-r border-border h-screen flex flex-col transition-transform lg:transition-all duration-300
          fixed lg:static top-0 left-0 z-50
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        data-testid="app-sidebar"
      >
      <div className={`p-6 border-b border-border flex ${collapsed ? 'justify-center' : 'justify-between'} items-center`}>
        {!collapsed && (
          <div>
            <h1 className="text-xl font-bold text-foreground tracking-tight">Sistema POS</h1>
            <p className="text-xs text-muted-foreground mt-1">{user?.name}</p>
            <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
            {myBranch && (
              <div
                className={`inline-flex items-center gap-1.5 mt-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${
                  myBranch.branch_short === 'M'
                    ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                    : 'bg-sky-100 text-sky-800 border border-sky-300'
                }`}
                data-testid="branch-badge"
                title={`Sucursal: ${myBranch.branch_name}`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                Sucursal: {myBranch.branch_name}
              </div>
            )}
          </div>
        )}
        <div className="flex items-center gap-1">
          {/* Cerrar en móvil */}
          <button
            onClick={onClose}
            className="lg:hidden p-2 hover:bg-slate-100 rounded transition-colors"
            title="Cerrar menú"
            data-testid="close-sidebar-btn"
          >
            <X size={20} />
          </button>
          {/* Colapsar en desktop */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="hidden lg:block p-2 hover:bg-slate-100 rounded transition-colors"
            title={collapsed ? 'Expandir menú' : 'Contraer menú'}
          >
            {collapsed ? <List size={20} /> : <X size={20} />}
          </button>
        </div>
      </div>

      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {(isClerk ? clerkVisibleItems : menuItems).map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.path.slice(1)}`}
              className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'} px-4 py-3 rounded-sm btn-transition ${
                isActive
                  ? 'bg-slate-100 border-l-4 border-primary text-foreground font-medium'
                  : 'text-muted-foreground hover:bg-slate-50 hover:text-foreground'
              }`}
              title={collapsed ? item.label : ''}
              onClick={() => onClose()}
            >
              <Icon size={20} weight={isActive ? 'fill' : 'regular'} />
              {!collapsed && (
                <>
                  <span className="text-sm flex-1">{item.label}</span>
                  {item.badge && (
                    <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-warning rounded-full">
                      {item.badge}
                    </span>
                  )}
                </>
              )}
            </Link>
          );
        })}

        {/* Sección "Oculto" — sólo cajeros. Requiere clave admin para abrir. */}
        {isClerk && clerkHiddenItems.length > 0 && (
          <div className="mt-2 pt-2 border-t border-slate-100">
            <button
              type="button"
              onClick={openHiddenToggle}
              data-testid="nav-hidden-toggle"
              className={`w-full flex items-center ${collapsed ? 'justify-center' : 'gap-3'} px-4 py-3 rounded-sm btn-transition text-muted-foreground hover:bg-slate-50 hover:text-foreground`}
              title={collapsed ? 'Oculto (requiere clave admin)' : ''}
            >
              {hiddenUnlocked ? <LockOpen size={20} /> : <Lock size={20} />}
              {!collapsed && (
                <>
                  <span className="text-sm flex-1 text-left">Oculto</span>
                  {hiddenUnlocked ? (
                    hiddenExpanded ? <CaretDown size={16} /> : <CaretRight size={16} />
                  ) : (
                    <span className="text-[10px] uppercase tracking-wide text-amber-600 font-semibold">Bloqueado</span>
                  )}
                </>
              )}
            </button>

            {hiddenUnlocked && hiddenExpanded && !collapsed && (
              <div className="mt-1 space-y-1 pl-2" data-testid="nav-hidden-list">
                {clerkHiddenItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      data-testid={`nav-${item.path.slice(1)}`}
                      className={`flex items-center gap-3 px-4 py-2.5 rounded-sm btn-transition text-xs ${
                        isActive
                          ? 'bg-slate-100 border-l-4 border-primary text-foreground font-medium'
                          : 'text-muted-foreground hover:bg-slate-50 hover:text-foreground'
                      }`}
                      onClick={() => onClose()}
                    >
                      <Icon size={16} weight={isActive ? 'fill' : 'regular'} />
                      <span className="flex-1">{item.label}</span>
                      {item.badge && (
                        <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-warning rounded-full">
                          {item.badge}
                        </span>
                      )}
                    </Link>
                  );
                })}
                <button
                  type="button"
                  onClick={lockHidden}
                  data-testid="nav-hidden-lock-btn"
                  className="flex items-center gap-2 px-4 py-2 text-[11px] text-amber-700 hover:text-amber-900 uppercase tracking-wide"
                >
                  <Lock size={14} /> Bloquear de nuevo
                </button>
              </div>
            )}
          </div>
        )}
      </nav>

      {/* Modal de desbloqueo (clave admin) */}
      {showUnlockModal && (
        <div
          className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
          onClick={() => !unlockLoading && setShowUnlockModal(false)}
          data-testid="unlock-modal-backdrop"
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitUnlock}
            className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6"
            data-testid="unlock-modal"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <Lock size={20} className="text-amber-700" />
              </div>
              <div>
                <h3 className="font-bold text-slate-800">Sección oculta</h3>
                <p className="text-xs text-slate-500">Ingresa la contraseña de administrador</p>
              </div>
            </div>
            <input
              type="password"
              autoFocus
              value={unlockPwd}
              onChange={(e) => { setUnlockPwd(e.target.value); setUnlockError(''); }}
              placeholder="Contraseña admin"
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="unlock-password-input"
              disabled={unlockLoading}
            />
            {unlockError && (
              <p className="text-xs text-rose-600 mt-2" data-testid="unlock-error">{unlockError}</p>
            )}
            <div className="flex items-center justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => setShowUnlockModal(false)}
                disabled={unlockLoading}
                className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-800"
                data-testid="unlock-cancel-btn"
              >Cancelar</button>
              <button
                type="submit"
                disabled={unlockLoading || !unlockPwd}
                className="px-4 py-1.5 text-sm font-medium bg-primary text-white rounded-md hover:opacity-90 disabled:opacity-50"
                data-testid="unlock-submit-btn"
              >{unlockLoading ? 'Verificando…' : 'Desbloquear'}</button>
            </div>
          </form>
        </div>
      )}

      <div className="p-4 border-t border-border space-y-1">
        {user?.role === 'admin' && (
          <Link
            to="/settings"
            data-testid="settings-link"
            className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'} px-4 py-3 w-full text-muted-foreground hover:bg-slate-50 hover:text-foreground rounded-sm btn-transition`}
            title={collapsed ? 'Configuración' : ''}
            onClick={() => onClose()}
          >
            <Gear size={20} />
            {!collapsed && <span className="text-sm">Configuración</span>}
          </Link>
        )}
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'} px-4 py-3 w-full text-muted-foreground hover:bg-slate-50 hover:text-foreground rounded-sm btn-transition`}
          title={collapsed ? 'Cerrar Sesión' : ''}
        >
          <SignOut size={20} />
          {!collapsed && <span className="text-sm">Cerrar Sesión</span>}
        </button>
      </div>
    </aside>
    </>
  );
};

export const MainLayout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  return (
    <div className="flex h-screen bg-background">
      <Sidebar mobileOpen={mobileOpen} onClose={() => setMobileOpen(false)} />
      <main className="flex-1 overflow-auto min-w-0">
        {/* Botón hamburguesa en móvil */}
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="lg:hidden sticky top-0 z-30 bg-white border-b border-border p-3 flex items-center gap-2 w-full text-left shadow-sm"
          data-testid="mobile-menu-btn"
        >
          <List size={22} weight="bold" />
          <span className="text-sm font-semibold">Menú</span>
        </button>
        {children}
      </main>
    </div>
  );
};
